import { Campaign } from '@/types/campaign.types';
import { EmailTypeDisplayNames, EmailTypeEnum } from '@/types/email.types';
import { TEMPLATE_WIDTH } from './editor/constants';

const STATUS_COLORS = {
  sent: '#22C55E',
  scheduled: '#3B82F6',
  draft: '#64748B',
} as const;

export const getCampaignStatusColor = (status: Campaign['state']) => {
  switch (status) {
    case 'published':
    case 'generated':
    case 'sent':
      return { color: STATUS_COLORS.sent, mix: `${STATUS_COLORS.sent}1A`, borderColor: STATUS_COLORS.sent };
    case 'scheduled':
      return {
        color: STATUS_COLORS.scheduled,
        mix: `${STATUS_COLORS.scheduled}1A`,
        borderColor: STATUS_COLORS.scheduled,
      };
    case 'draft':
    default:
      return { color: STATUS_COLORS.draft, mix: `${STATUS_COLORS.draft}1A`, borderColor: STATUS_COLORS.draft };
  }
};

export const getCampaignStatusLabel = (status: Campaign['state']) => {
  switch (status) {
    case 'published':
    case 'generated':
      return 'Published';
    case 'sent':
      return 'Sent';
    case 'scheduled':
      return 'Scheduled';
    case 'draft':
    default:
      return 'Draft';
  }
};

export const campaignSelectTypes = (regDisabled = false) => [
  {
    label: EmailTypeDisplayNames[EmailTypeEnum.event_registration_email],
    value: EmailTypeEnum.event_registration_email,
    isDisabled: regDisabled,
  },
  { label: EmailTypeDisplayNames[EmailTypeEnum.event_invite], value: EmailTypeEnum.event_invite },
  { label: EmailTypeDisplayNames[EmailTypeEnum.session_ended], value: EmailTypeEnum.session_ended },
  { label: EmailTypeDisplayNames[EmailTypeEnum.session_reminder], value: EmailTypeEnum.session_reminder },
  { label: EmailTypeDisplayNames[EmailTypeEnum.session_live], value: EmailTypeEnum.session_live },
];

const ALLOWED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png'];
export const isIllegalImageType = (file: File) => {
  for (const mime of ALLOWED_IMAGE_MIME_TYPES) {
    if (file.type.includes(mime)) {
      return false;
    }
  }
  return true;
};

const PLAY_BUTTON_SRC = 'https://assets.brandlive.com/assets/emails/play-square.png';

const CANVAS_WIDTH = TEMPLATE_WIDTH;

const PLAY_BUTTON_WIDTH = 32;

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`could not load ${src}`));
    image.crossOrigin = 'anonymous';
    image.src = src;
  });
}

export async function renderImageWithPlayButton(file: File) {
  const objectURL = URL.createObjectURL(file);

  try {
    const [playButton, image] = await Promise.all([loadImage(PLAY_BUTTON_SRC), loadImage(objectURL)]);

    if (!image.naturalWidth || !image.naturalHeight) return undefined;

    const width = CANVAS_WIDTH;
    const height = Math.round((width * image.naturalHeight) / image.naturalWidth);

    const canvas = new OffscreenCanvas(width, height);
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    ctx.drawImage(image, 0, 0, width, height);

    const buttonWidth = PLAY_BUTTON_WIDTH;
    const buttonHeight = Math.round(buttonWidth * (playButton.naturalHeight / playButton.naturalWidth));
    ctx.drawImage(playButton, (width - buttonWidth) / 2, (height - buttonHeight) / 2, buttonWidth, buttonHeight);

    return await canvas.convertToBlob({ type: 'image/jpeg', quality: 0.92 });
  } catch (e) {
    console.error(e);
    return undefined;
  } finally {
    URL.revokeObjectURL(objectURL);
  }
}
